/**
 * Macro #24 — global Activity notification bell (authenticated top bar).
 *
 * Premium, non-invasive. Shows an authoritative unread badge; a click opens a
 * drawer/popover of the most recent events (newest first, read/unread visual
 * distinction, relative time). Clicking an item marks it read and deep-links to
 * the relevant product context. "Mark all read" + "View All" (→ Profile ▸
 * Activity) footer. No fake/frontend-generated events; the bell only renders for
 * a connected wallet and fails-closed with no session.
 */
import { useEffect, useRef, useState } from "react";
import { useAccount } from "wagmi";
import { activityApi, type ActivityEvent } from "../lib/activity-api";
import {
  ACTIVITY_READ_EVENT,
  notifyActivityRead,
  useActivityNotifications,
} from "./useActivityNotifications";
import { CATEGORY_TINT, gotoDeepLink, timeAgo } from "./activity-format";
import { cn } from "./cn";

export function NotificationBell() {
  const { isConnected } = useAccount();
  const { unreadCount, recent, refresh } = useActivityNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!isConnected) return null;

  const onItem = async (ev: ActivityEvent) => {
    setOpen(false);
    if (!ev.read) {
      try {
        await activityApi.markRead(ev.id);
        notifyActivityRead();
      } catch {
        /* fail-closed; badge refreshes on next poll */
      }
    }
    gotoDeepLink(ev);
  };

  const onMarkAll = async () => {
    try {
      await activityApi.markAllRead();
      notifyActivityRead();
      refresh();
    } catch {
      /* ignore */
    }
  };

  const onViewAll = () => {
    setOpen(false);
    window.location.hash = "profile/activity";
    window.dispatchEvent(new Event(ACTIVITY_READ_EVENT));
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="notif-bell-btn"
        aria-label={unreadCount > 0 ? `Notifications — ${unreadCount} unread` : "Notifications"}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "relative inline-flex items-center justify-center h-7 w-7 rounded-md",
          "text-text-secondary hover:text-text-primary transition-colors duration-fast",
          "neon-glass pill-ghost",
        )}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
          <path
            d="M8 2a3.2 3.2 0 0 0-3.2 3.2v1.9c0 .6-.24 1.18-.66 1.6L3 10h10l-1.14-1.3a2.27 2.27 0 0 1-.66-1.6V5.2A3.2 3.2 0 0 0 8 2Z"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinejoin="round"
          />
          <path d="M6.4 12a1.6 1.6 0 0 0 3.2 0" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
        {unreadCount > 0 && (
          <span
            data-testid="notif-bell-badge"
            className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 inline-flex items-center justify-center rounded-full bg-neon-coral text-[9px] font-mono font-semibold text-black leading-none"
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-bell-drawer"
          role="dialog"
          aria-label="Notifications"
          className={cn(
            "absolute right-0 mt-2 z-modal w-[min(360px,90vw)]",
            "rounded-2xl border border-white/10 bg-[rgba(12,16,22,0.96)] backdrop-blur-xl",
            "shadow-[0_24px_60px_-20px_rgba(0,0,0,0.95)] overflow-hidden",
          )}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60">
              Notifications
            </span>
            {unreadCount > 0 && (
              <button
                type="button"
                data-testid="notif-bell-mark-all"
                onClick={onMarkAll}
                className="font-mono text-[10px] uppercase tracking-[0.12em] text-teal-200 hover:text-teal-100"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {recent.length === 0 ? (
              <div
                data-testid="notif-bell-empty"
                className="px-4 py-8 text-center text-[12px] text-white/45"
              >
                {"You're all caught up."}
              </div>
            ) : (
              recent.map((ev) => (
                <button
                  key={ev.id}
                  type="button"
                  data-testid="notif-bell-item"
                  data-read={ev.read}
                  onClick={() => onItem(ev)}
                  className={cn(
                    "block w-full text-left px-4 py-3 border-b border-white/[0.06]",
                    "transition-colors hover:bg-white/[0.04]",
                    ev.read ? "opacity-70" : "",
                  )}
                >
                  <div className="flex items-start gap-2">
                    {!ev.read && (
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-neon-coral" aria-hidden />
                    )}
                    <div className={cn("min-w-0 flex-1", ev.read && "pl-3.5")}>
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className={cn(
                            "inline-flex items-center rounded-full border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.1em]",
                            CATEGORY_TINT[ev.category],
                          )}
                        >
                          {ev.category}
                        </span>
                        <span className="shrink-0 font-mono text-[10px] text-white/40">{timeAgo(ev.event_time)}</span>
                      </div>
                      <div className="mt-1 text-[13px] font-medium text-white/90 truncate">{ev.title}</div>
                      <div className="text-[12px] text-white/55 line-clamp-2">{ev.body}</div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>

          <button
            type="button"
            data-testid="notif-bell-view-all"
            onClick={onViewAll}
            className="block w-full px-4 py-3 border-t border-white/10 font-mono text-[11px] uppercase tracking-[0.14em] text-white/70 hover:text-white hover:bg-white/[0.04] transition-colors"
          >
            View all activity →
          </button>
        </div>
      )}
    </div>
  );
}
